import React from 'react';
import { CircleDollarSign, TrendingUp, LineChart, Coins, BarChart3 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Animated BBA ambient background for the portal dashboard, ported from the
 * "Portal Background" Claude Design file: warm radial base, sliding grid,
 * spinning + drifting brand emblems, self-drawing profit lines, growing bar
 * charts, rising finance icons, a red light sweep, and a soft vignette.
 * Purely decorative — fixed, pointer-events: none, behind the page content.
 */

// Theme palettes mirror the design file's light/dark CSS variable sets.
const LIGHT = {
  bg1: '#FAF9F7', bg2: '#F2F0ED', bg3: '#EAE7E3',
  ink: 'rgba(20,17,15,0.10)', inkSoft: 'rgba(20,17,15,0.055)', inkStrong: 'rgba(20,17,15,0.16)',
  emblem: 0.045, emblemFilter: 'none',
  redLine: 'rgba(248,7,7,0.55)', redGlow: 'rgba(248,7,7,0.14)',
  redBar: 'rgba(248,7,7,0.42)', redBar2: 'rgba(248,7,7,0.62)',
  sweep: 'rgba(248,7,7,0.05)', vignette: 'rgba(20,17,15,0.05)',
};
const DARK = {
  bg1: '#221d19', bg2: '#14110f', bg3: '#0d0b09',
  ink: 'rgba(255,255,255,0.13)', inkSoft: 'rgba(255,255,255,0.045)', inkStrong: 'rgba(255,255,255,0.20)',
  emblem: 0.07, emblemFilter: 'brightness(0) invert(1)', // emblem ships red; flatten to white
  redLine: 'rgba(248,7,7,0.8)', redGlow: 'rgba(248,7,7,0.22)',
  redBar: 'rgba(248,7,7,0.55)', redBar2: 'rgba(248,7,7,0.9)',
  sweep: 'rgba(248,7,7,0.06)', vignette: 'rgba(13,11,9,0.45)',
};

// Rising finance icons: [Icon, left%, size, color-var, duration s, delay s]
const RISING_ICONS = [
  [CircleDollarSign, '5%', 28, 'var(--bbg-red-bar)', 18, -2],
  [TrendingUp, '14%', 22, 'var(--bbg-ink-strong)', 24, -9],
  [LineChart, '22%', 18, 'var(--bbg-ink)', 29, -20],
  [Coins, '79%', 24, 'var(--bbg-red-bar)', 21, -6],
  [BarChart3, '88%', 22, 'var(--bbg-ink-strong)', 23, -16],
  [CircleDollarSign, '95%', 18, 'var(--bbg-red-bar)', 16, -8],
];

const CHART_MAIN = 'M 10 470 L 130 420 L 240 445 L 370 330 L 480 360 L 610 190 L 790 60';
const CHART_ALT = 'M 10 480 L 150 400 L 260 430 L 420 280 L 540 320 L 680 140 L 790 40';

// `forceDark` pins the dark palette for always-dark surfaces (e.g. the public
// /welcome/JTBootcamp page), which must not follow the viewer's theme toggle.
const PortalBackground = ({ showGrid = true, showMetrics = true, forceDark = false }) => {
  const { darkMode } = useTheme();
  const t = forceDark || darkMode ? DARK : LIGHT;

  const vars = {
    '--bbg-ink': t.ink,
    '--bbg-ink-soft': t.inkSoft,
    '--bbg-ink-strong': t.inkStrong,
    '--bbg-emblem': t.emblem,
    '--bbg-red-line': t.redLine,
    '--bbg-red-glow': t.redGlow,
    '--bbg-red-bar': t.redBar,
    '--bbg-red-bar-2': t.redBar2,
    '--bbg-sweep': t.sweep,
    '--bbg-vignette': t.vignette,
  };

  return (
    <div
      className="bba-pbg"
      aria-hidden="true"
      style={{
        ...vars,
        background: `radial-gradient(120% 90% at 70% 30%, ${t.bg1} 0%, ${t.bg2} 62%, ${t.bg3} 100%)`,
      }}
    >
      <style>{`
      .bba-pbg { position: fixed; inset: 0; overflow: hidden; pointer-events: none; }

      @keyframes bbaPbgSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes bbaPbgPulse { 0%, 100% { opacity: var(--bbg-emblem); } 50% { opacity: calc(var(--bbg-emblem) * 1.7); } }
      @keyframes bbaPbgDrift {
        0%, 100% { transform: translate(0, 0) rotate(0deg) scale(1); }
        50% { transform: translate(2vw, -4vh) rotate(14deg) scale(1.06); }
      }
      @keyframes bbaPbgGridSlide { from { background-position: 0 0; } to { background-position: 0 -64px; } }
      @keyframes bbaPbgDraw { from { stroke-dashoffset: 900; } to { stroke-dashoffset: 0; } }
      @keyframes bbaPbgGrow {
        0%, 12% { transform: scaleY(0.12); }
        55%, 78% { transform: scaleY(1); }
        100% { transform: scaleY(0.12); }
      }
      @keyframes bbaPbgRise {
        0% { transform: translateY(112vh) translateX(0) rotate(-8deg); opacity: 0; }
        8% { opacity: 1; }
        50% { transform: translateY(45vh) translateX(3vw) rotate(6deg); }
        92% { opacity: 1; }
        100% { transform: translateY(-18vh) translateX(-2vw) rotate(-4deg); opacity: 0; }
      }
      @keyframes bbaPbgSweep {
        0% { transform: translateX(-30vw) rotate(-24deg); opacity: 0; }
        15%, 85% { opacity: 1; }
        100% { transform: translateX(130vw) rotate(-24deg); opacity: 0; }
      }

      .bba-pbg__grid {
        position: absolute; inset: -64px 0 0 0;
        background-image:
          linear-gradient(var(--bbg-ink-soft) 1px, transparent 1px),
          linear-gradient(90deg, var(--bbg-ink-soft) 1px, transparent 1px);
        background-size: 64px 64px;
        animation: bbaPbgGridSlide 14s linear infinite;
      }
      .bba-pbg__emblem-spin {
        position: absolute; top: 50%; left: 82%; width: 820px; height: 820px; margin: -410px 0 0 -410px;
        background: url('/bba-emblem.png') center / contain no-repeat;
        animation: bbaPbgSpin 240s linear infinite, bbaPbgPulse 9s ease-in-out infinite;
      }
      .bba-pbg__emblem-drift {
        position: absolute; bottom: -150px; left: -130px; width: 440px; height: 440px;
        opacity: var(--bbg-emblem);
        background: url('/bba-emblem.png') center / contain no-repeat;
        animation: bbaPbgDrift 22s ease-in-out infinite;
      }
      .bba-pbg__bar { transform-origin: bottom; border-radius: 3px 3px 0 0; }
      .bba-pbg__rise { position: absolute; bottom: 0; }

      @media (prefers-reduced-motion: reduce) {
        .bba-pbg *, .bba-pbg *::before, .bba-pbg *::after { animation: none !important; }
      }
      `}</style>

      {showGrid && <div className="bba-pbg__grid" />}

      <div className="bba-pbg__emblem-spin" style={{ filter: t.emblemFilter }} />
      <div className="bba-pbg__emblem-drift" style={{ filter: t.emblemFilter }} />

      {showMetrics && (
        <>
          {/* self-drawing red profit line, lower left */}
          <div style={{ position: 'absolute', left: '1.5vw', bottom: '7vh', width: '24vw', height: '46vh' }}>
            <svg viewBox="0 0 800 500" style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
              <path
                d={CHART_MAIN}
                fill="none" stroke="var(--bbg-red-glow)" strokeWidth="16" strokeLinecap="round"
                strokeLinejoin="round" strokeDasharray="900"
                style={{ animation: 'bbaPbgDraw 7s cubic-bezier(0.2,0,0.2,1) infinite alternate', filter: 'blur(7px)' }}
              />
              <path
                d={CHART_MAIN}
                fill="none" stroke="var(--bbg-red-line)" strokeWidth="4" strokeLinecap="round"
                strokeLinejoin="round" strokeDasharray="900"
                style={{ animation: 'bbaPbgDraw 7s cubic-bezier(0.2,0,0.2,1) infinite alternate' }}
              />
            </svg>
          </div>

          {/* second ink line, upper right */}
          <div style={{ position: 'absolute', right: '1.5vw', top: '5vh', width: '22vw', height: '30vh' }}>
            <svg viewBox="0 0 800 500" style={{ width: '100%', height: '100%' }} preserveAspectRatio="none">
              <path
                d={CHART_ALT}
                fill="none" stroke="var(--bbg-ink-strong)" strokeWidth="4" strokeLinecap="round"
                strokeLinejoin="round" strokeDasharray="900"
                style={{ animation: 'bbaPbgDraw 9s cubic-bezier(0.2,0,0.2,1) -3s infinite alternate' }}
              />
            </svg>
          </div>

          {/* bar cluster hanging from the top left */}
          <div style={{ position: 'absolute', left: '3vw', top: 0, display: 'flex', alignItems: 'flex-start', gap: 12, height: '19vh', transform: 'rotate(180deg)' }}>
            <div className="bba-pbg__bar" style={{ width: 16, height: '40%', background: 'var(--bbg-ink)', animation: 'bbaPbgGrow 9s ease-in-out -1s infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 16, height: '62%', background: 'var(--bbg-ink)', animation: 'bbaPbgGrow 9s ease-in-out -0.4s infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 16, height: '52%', background: 'var(--bbg-ink)', animation: 'bbaPbgGrow 9s ease-in-out 0.4s infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 16, height: '84%', background: 'linear-gradient(to bottom, var(--bbg-red-bar), var(--bbg-red-bar-2))', animation: 'bbaPbgGrow 9s ease-in-out 1s infinite' }} />
          </div>

          {/* rising bar cluster, bottom right */}
          <div style={{ position: 'absolute', right: '2vw', bottom: 0, display: 'flex', alignItems: 'flex-end', gap: 16, height: '38vh' }}>
            <div className="bba-pbg__bar" style={{ width: 22, height: '34%', background: 'var(--bbg-ink)', animation: 'bbaPbgGrow 8s ease-in-out infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 22, height: '48%', background: 'var(--bbg-ink)', animation: 'bbaPbgGrow 8s ease-in-out 0.5s infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 22, height: '42%', background: 'var(--bbg-ink)', animation: 'bbaPbgGrow 8s ease-in-out 1s infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 22, height: '66%', background: 'var(--bbg-ink-strong)', animation: 'bbaPbgGrow 8s ease-in-out 1.5s infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 22, height: '82%', background: 'var(--bbg-ink-strong)', animation: 'bbaPbgGrow 8s ease-in-out 2s infinite' }} />
            <div className="bba-pbg__bar" style={{ width: 22, height: '100%', background: 'linear-gradient(to top, var(--bbg-red-bar), var(--bbg-red-bar-2))', animation: 'bbaPbgGrow 8s ease-in-out 2.5s infinite' }} />
          </div>

          {/* finance icons floating up through the page */}
          {RISING_ICONS.map(([Icon, left, size, color, duration, delay], i) => (
            <div
              key={i}
              className="bba-pbg__rise"
              style={{ left, animation: `bbaPbgRise ${duration}s linear ${delay}s infinite` }}
            >
              <Icon size={size} style={{ color }} />
            </div>
          ))}
        </>
      )}

      {/* red light sweep */}
      <div
        style={{
          position: 'absolute', top: '-20vh', left: 0, width: '18vw', height: '140vh',
          background: 'linear-gradient(90deg, rgba(248,7,7,0) 0%, var(--bbg-sweep) 50%, rgba(248,7,7,0) 100%)',
          animation: 'bbaPbgSweep 16s ease-in-out infinite',
        }}
      />

      {/* vignette */}
      <div
        style={{
          position: 'absolute', inset: 0,
          background: 'radial-gradient(58% 62% at 50% 45%, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 55%, var(--bbg-vignette) 100%)',
        }}
      />
    </div>
  );
};

export default PortalBackground;
